import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { API_URL } from "../constants";
import { getToken } from "../utils/auth";
import {
  PAID_ATTEMPT_STORAGE_MESSAGE,
  clearPaidAttempt,
  createPaidAttempt,
  loadPaidAttempt,
  paidAttemptEndpoint,
  paidAttemptKey,
  parsePaidReceipt,
} from "../utils/paidAttempts";

export const PAID_POST_TIMEOUT_MS = 30000;
export const PAID_STATUS_TIMEOUT_MS = 10000;
export const PAID_ATTEMPT_UNKNOWN_MESSAGE =
  "We could not confirm this purchase request yet. Its identity is saved. Check status before starting another request.";

/** @typedef {import("../utils/paidAttempts").PaidAttempt} PaidAttempt */
/** @typedef {import("../utils/paidAttempts").PaidReceipt} PaidReceipt */
/** @typedef {import("../utils/paidAttempts").PaidScope} PaidScope */
/**
 * A paid POST is allowed only after durable reservation. Recovery is GET-only;
 * the optional retryConfirmed method deliberately resends the SAME identity.
 * Callers retain accepted-running records until exact terminal state is adopted.
 *
 * @param {{scope:PaidScope, enabled:boolean,
 * onAccepted?:(receipt:PaidReceipt,record:PaidAttempt)=>void|Promise<void>,
 * onRejected?:(receipt:PaidReceipt,record:PaidAttempt)=>void|Promise<void>}} options
 */
const usePaidAttempt = ({ scope, enabled, onAccepted, onRejected }) => {
  const [phase, setPhase] = useState("idle");
  const [pending, setPending] = useState(/** @type {PaidAttempt|null} */ (null));
  const [receipt, setReceipt] = useState(/** @type {PaidReceipt|null} */ (null));
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const mounted = useRef(false);
  const epoch = useRef(0);
  const operation = useRef(
    /** @type {{controller:AbortController,promise:Promise<PaidReceipt|null>}|null} */ (null)
  );
  const current = useRef({ scope, enabled, onAccepted, onRejected });
  current.current = { scope, enabled, onAccepted, onRejected };
  const recordRef = useRef(/** @type {PaidAttempt|null} */ (null));
  const receiptToken = useRef("");
  const ownerToken = useRef("");
  // Invalid/missing targets are normal while an owner page loads. They never
  // enable storage access or a request.
  let scopeKey = "";
  try {
    scopeKey = paidAttemptKey(scope);
  } catch {
    /* unavailable target */
  }
  const liveKey = useRef(scopeKey);
  liveKey.current = scopeKey;

  const acknowledge = useCallback((/** @type {string} */ expectedAttemptId) => {
    const record = recordRef.current;
    if (
      !mounted.current ||
      !current.current.enabled ||
      !record ||
      record.attempt_id !== expectedAttemptId ||
      !receiptToken.current ||
      getToken() !== receiptToken.current
    )
      return false;
    if (!clearPaidAttempt(current.current.scope, expectedAttemptId)) {
      setError(PAID_ATTEMPT_STORAGE_MESSAGE);
      setPhase("blocked");
      return false;
    }
    recordRef.current = null;
    setPending(null);
    setReceipt(null);
    setError("");
    setPhase("idle");
    return true;
  }, []);

  const run = useCallback(
    /** @param {"submit"|"check"|"retry"} mode @param {Record<string,any>} [request] */
    (mode, request) => {
      if (operation.current) return operation.current.promise;
      const { scope: activeScope, enabled: activeEnabled } = current.current;
      const token = getToken();
      if (!mounted.current || !activeEnabled || !token || !liveKey.current)
        return Promise.resolve(null);
      if (token !== ownerToken.current) {
        setPhase("unknown");
        setError(
          "Your session changed. Reload this listing before checking the saved purchase request."
        );
        return Promise.resolve(null);
      }
      const capturedEpoch = epoch.current;
      const key = liveKey.current;
      const controller = new AbortController();
      const alive = () =>
        mounted.current &&
        current.current.enabled &&
        epoch.current === capturedEpoch &&
        liveKey.current === key &&
        getToken() === token &&
        !controller.signal.aborted;
      /** @type {PaidAttempt|null} */
      let record = null;
      let shouldPost = false;
      try {
        if (mode === "submit") {
          // A saved request takes precedence over live UI choices. Merely
          // clicking again can only check it, never replace or resend it.
          record = loadPaidAttempt(activeScope);
          if (!record && recordRef.current) throw new Error(PAID_ATTEMPT_STORAGE_MESSAGE);
          if (!record) {
            const reserved = createPaidAttempt(activeScope, request);
            record = reserved.attempt;
            shouldPost = reserved.created;
          }
        } else {
          record = loadPaidAttempt(activeScope);
          shouldPost = mode === "retry" && !!record;
        }
        if (!record) {
          if (recordRef.current) throw new Error(PAID_ATTEMPT_STORAGE_MESSAGE);
          setPhase("idle");
          setError("");
          return Promise.resolve(null);
        }
      } catch (failure) {
        setPhase("blocked");
        setError(failure instanceof Error ? failure.message : PAID_ATTEMPT_STORAGE_MESSAGE);
        return Promise.resolve(null);
      }
      const capturedRecord = record;
      recordRef.current = record;
      setPending(record);
      setError("");
      setBusy(true);
      setPhase(shouldPost ? "submitting" : "checking");
      const config = { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal };
      /** @param {any} data */
      const adopt = async (data) => {
        const validated = parsePaidReceipt(data, capturedRecord);
        if (!alive()) return null;
        // The durable record must still be ours before an old response can
        // adopt output or clean up a newer request.
        if (loadPaidAttempt(activeScope)?.attempt_id !== capturedRecord.attempt_id)
          throw new Error("The saved purchase request changed. Check status again.");
        receiptToken.current = token;
        setReceipt(validated);
        setPhase(validated.attempt.status);
        if (validated.attempt.status === "accepted")
          await current.current.onAccepted?.(validated, capturedRecord);
        else await current.current.onRejected?.(validated, capturedRecord);
        return alive() ? validated : null;
      };
      const execute = async () => {
        try {
          // Reservation and network dispatch are separated by a microtask.
          // Revalidate immediately before any request, not only after it.
          if (!alive()) return null;
          const saved = loadPaidAttempt(activeScope);
          if (
            saved?.attempt_id !== capturedRecord.attempt_id ||
            JSON.stringify(saved.request) !== JSON.stringify(capturedRecord.request)
          )
            throw new Error("The saved purchase request changed before dispatch.");
          if (shouldPost) {
            try {
              const response = await axios.post(
                `${API_URL}${paidAttemptEndpoint(capturedRecord)}`,
                { ...capturedRecord.request, attempt_id: capturedRecord.attempt_id },
                { ...config, timeout: PAID_POST_TIMEOUT_MS }
              );
              if (!alive()) return null;
              return await adopt(response.data);
            } catch (failure) {
              if (!alive()) return null;
              // HTTP rejection is definitive only with a matching durable
              // receipt. Missing/malformed responses require exact lookup.
              const data = /** @type {any} */ (failure)?.response?.data;
              if (data) {
                try {
                  return await adopt(data);
                } catch {
                  /* exact GET below */
                }
              }
              setPhase("checking");
            }
          }
          const response = await axios.get(
            `${API_URL}/purchase-attempts/${capturedRecord.attempt_id}/`,
            { ...config, timeout: PAID_STATUS_TIMEOUT_MS }
          );
          if (!alive()) return null;
          return await adopt(response.data);
        } catch (_failure) {
          if (!alive()) return null;
          setPhase("unknown");
          setError(PAID_ATTEMPT_UNKNOWN_MESSAGE);
          return null;
        } finally {
          if (operation.current?.controller === controller) operation.current = null;
          if (mounted.current && epoch.current === capturedEpoch && liveKey.current === key) {
            setBusy(false);
            if (getToken() !== token) {
              setPhase("unknown");
              setReceipt(null);
              setError(
                "Your session changed. Reload this listing before checking the saved purchase request."
              );
            }
          }
        }
      };
      // Defer execution one microtask so same-tick clicks see the guard before
      // any callback can resolve, reject, or acknowledge the record.
      const promise = Promise.resolve().then(execute);
      operation.current = { controller, promise };
      return promise;
    },
    []
  );
  const submit = useCallback(
    (/** @type {Record<string,any>} */ request) => run("submit", request),
    [run]
  );
  const check = useCallback(() => run("check"), [run]);
  const retryConfirmed = useCallback(() => run("retry"), [run]);

  useEffect(() => {
    mounted.current = true;
    epoch.current += 1;
    operation.current?.controller.abort();
    operation.current = null;
    recordRef.current = null;
    receiptToken.current = "";
    ownerToken.current = enabled ? getToken() || "" : "";
    setPending(null);
    setReceipt(null);
    setError("");
    setBusy(false);
    setPhase("idle");
    if (enabled && scopeKey) void check();
    return () => {
      mounted.current = false;
      epoch.current += 1;
      operation.current?.controller.abort();
      operation.current = null;
    };
  }, [enabled, scopeKey, check]);

  const visible =
    enabled &&
    getToken() === ownerToken.current &&
    pending?.target_uuid === scope.targetUuid &&
    pending?.action === scope.action &&
    pending?.artifact === (scope.artifact || null);
  return {
    phase,
    busy,
    pending: visible ? pending : null,
    receipt: visible ? receipt : null,
    error,
    submit,
    check,
    retryConfirmed,
    acknowledge,
  };
};

export default usePaidAttempt;
